import React from 'react';
import { TPEReactTable } from 'src/components/shared/TPEReactTable';
import TPENavigator from 'src/components/TPENavigator';
import { makeNameLinkColumn } from 'src/components/custom-tables-shared/TableNameLinkColumn';
import { SegmentTableListRow } from 'src/models/segment-cdt/SegmentTableListRow';

const ACTIVE_STATUS_COLOR = '#1d8102';

/**
 * Read-only grid for the Segment Data Tables list: Name (link) · Segment Type · Status · Owner ·
 * Last Pull. Dedicated (not the CLI `TablesGrid`) because the column set + data model differ and
 * this grid is expected to diverge further; the reusable Name-link cell is shared via
 * makeNameLinkColumn. TPEReactTable renders the loading + empty states.
 */
export default function SegmentTablesGrid(props: { loading: boolean, tables: SegmentTableListRow[], tableDetailUrl: string }) {
    const { loading, tables = [], tableDetailUrl } = props;
    const [navigateURL, setNavigateURL] = React.useState('');

    // TODO(next CR): the segment detail route/view is not registered yet, so this navigation
    // currently no-matches. The Name-link wiring is kept so it works unchanged once the detail
    // page and its <Route> land in the upcoming detail CR. Safe today: the tab is flag-gated.
    const navigateToDetail = (tableId: string) => setNavigateURL(`${tableDetailUrl}/${btoa(tableId)}`);

    const columnDefinitions = React.useMemo(() => [
        makeNameLinkColumn({ navigateToDetail, accessor: 'name', idAccessor: 'tableId', header: 'Name' }),
        {
            accessor: 'segmentTypeDisplay',
            Header: () => <div className="cell-text">Segment Type</div>,
            Cell: (e: any) => <div className="cell-text">{e.value}</div>,
        },
        {
            accessor: 'status',
            Header: () => <div className="cell-text">Status</div>,
            // Rendered only from the response; blank if absent (no confirmed backend field — OQ-03b).
            Cell: (e: any) => e.value ? <span style={{ color: ACTIVE_STATUS_COLOR, fontWeight: 600 }}>{e.value}</span> : null,
        },
        {
            accessor: 'owner',
            Header: () => <div className="cell-text">Owner</div>,
            Cell: (e: any) => <div className="cell-text">{e.value}</div>,
        },
        {
            accessor: 'lastPullDisplay',
            Header: () => <div className="cell-text">Last Pull</div>,
            Cell: (e: any) => <div className="cell-text">{e.value}</div>,
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], []);

    return (
        <div>
            <TPENavigator path={navigateURL} />
            <TPEReactTable
                className="segmentDataTablesGrid"
                isSearching={loading}
                data={tables}
                columnDefinitions={columnDefinitions}
                sortable={false}
                emptyMessage="No segment data tables yet."
            />
        </div>
    );
}
